import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { courses } from '@/api/courses';
import type {
  CourseRow, CourseSectionRow, EnrollmentRow, CourseSectionKind,
} from '@/api/courses';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { formatRef } from '@/lib/formatRef';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ChevronLeft, Plus, Trash2, GripVertical, BookOpen, Users as UsersIcon,
  Mic, GraduationCap, ClipboardList, PhoneCall,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { CourseSectionTargetPicker } from '@/components/CourseSectionTargetPicker';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function CourseDetail() {
  const { id } = useParams();
  const user = useCurrentUser();
  const canMutate = CAN_MUTATE.includes(user.role);
  const [tab, setTab] = useState<'syllabus' | 'enrollments'>('syllabus');

  const { data: course, isLoading, isError } = useQuery({
    queryKey: ['course', id],
    queryFn: () => courses.get(id!),
    enabled: !!id,
  });

  if (!id) return <Navigate to="/courses" replace />;
  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (isError || !course) {
    return (
      <Card>
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          Course not found or no access.
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="mb-2">
        <Link
          to="/courses"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-3 w-3" />
          All courses
        </Link>
      </div>

      <PageHeader
        eyebrow={formatRef(course.ref_number, 'CRS')}
        title={course.title}
        description={course.description ?? undefined}
      />

      <CourseMeta course={course} />

      <div className="mt-6 flex items-center gap-1 border-b border-border">
        <TabButton active={tab === 'syllabus'} onClick={() => setTab('syllabus')}>
          <BookOpen className="h-3.5 w-3.5 mr-1.5" />
          Syllabus · {course.section_count}
        </TabButton>
        <TabButton active={tab === 'enrollments'} onClick={() => setTab('enrollments')}>
          <UsersIcon className="h-3.5 w-3.5 mr-1.5" />
          Enrollments · {course.enrollment_count}
        </TabButton>
      </div>

      <div className="mt-5">
        {tab === 'syllabus'
          ? <SyllabusPanel course={course} canMutate={canMutate} />
          : <EnrollmentsPanel course={course} canMutate={canMutate} />}
      </div>
    </>
  );
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors',
        'border-b-2 -mb-px',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function CourseMeta({ course }: { course: CourseRow }) {
  return (
    <Card className="mt-4">
      <CardContent className="py-5 flex items-center gap-8 flex-wrap">
        <Stat
          label="Price"
          value={course.price_cents > 0
            ? `${course.currency} ${(course.price_cents / 100).toFixed(2)}`
            : 'Free'}
        />
        <Divider />
        <Stat
          label="Enrollment"
          value={course.max_enrollment
            ? `${course.enrollment_count} / ${course.max_enrollment}`
            : String(course.enrollment_count)}
        />
        <Divider />
        <Stat label="Sections" value={String(course.section_count)} />
        {course.starts_at && (
          <>
            <Divider />
            <Stat label="Starts" value={new Date(course.starts_at).toLocaleDateString()} />
          </>
        )}
        {course.ends_at && (
          <>
            <Divider />
            <Stat label="Ends" value={new Date(course.ends_at).toLocaleDateString()} />
          </>
        )}
        <Divider />
        <Stat
          label="Status"
          value={course.active ? 'Active' : 'Inactive'}
          valueClassName={course.active ? 'text-emerald-700' : 'text-slate-400'}
        />
      </CardContent>
    </Card>
  );
}

function Stat({
  label, value, valueClassName,
}: { label: string; value: string; valueClassName?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">{label}</div>
      <div className={cn('font-mono tabular-nums text-base font-semibold', valueClassName)}>{value}</div>
    </div>
  );
}

function Divider() {
  return <div className="h-8 w-px bg-border" />;
}

// ── Syllabus ─────────────────────────────────────────────────────────────────

function SyllabusPanel({
  course, canMutate,
}: { course: CourseRow; canMutate: boolean }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['course-sections', course.id],
    queryFn: () => courses.listSections(course.id),
  });
  const sections = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => courses.removeSection(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['course-sections', course.id] });
      qc.invalidateQueries({ queryKey: ['course', course.id] });
    },
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-3">
      {sections.length === 0 ? (
        <SectionsEmpty onAdd={canMutate ? () => setAddOpen(true) : undefined} />
      ) : (
        <>
          <ol className="space-y-2">
            {sections.map((s, i) => (
              <SectionItem
                key={s.id}
                index={i + 1}
                section={s}
                canMutate={canMutate}
                onDelete={() => remove.mutate(s.id)}
                removing={remove.isPending}
              />
            ))}
          </ol>
          {canMutate && (
            <Button
              size="sm" variant="outline"
              onClick={() => setAddOpen(true)}
              className="w-full border-dashed"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add a section
            </Button>
          )}
        </>
      )}

      {canMutate && (
        <AddSectionDialog
          courseId={course.id}
          customerId={course.customer_id}
          nextSortOrder={sections.length}
          open={addOpen}
          onOpenChange={setAddOpen}
        />
      )}
    </div>
  );
}

function SectionsEmpty({ onAdd }: { onAdd?: () => void }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <BookOpen className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-500">No sections yet</p>
        <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
          A section points at a folder (listening material), conference (live
          class), quiz, or survey. Add sections to build a syllabus.
        </p>
        {onAdd && (
          <Button size="sm" className="mt-4" onClick={onAdd}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add the first section
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

const SECTION_KIND_META: Record<CourseSectionKind, {
  icon: typeof Mic;
  label: string;
  style: string;
}> = {
  folder:     { icon: Mic,            label: 'Folder',     style: 'bg-sky-50 text-sky-700 ring-sky-200' },
  conference: { icon: PhoneCall,      label: 'Conference', style: 'bg-violet-50 text-violet-700 ring-violet-200' },
  quiz:       { icon: GraduationCap,  label: 'Quiz',       style: 'bg-amber-50 text-amber-800 ring-amber-200' },
  survey:     { icon: ClipboardList,  label: 'Survey',     style: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
};

function SectionItem({
  index, section, canMutate, onDelete, removing,
}: {
  index: number;
  section: CourseSectionRow;
  canMutate: boolean;
  onDelete: () => void;
  removing: boolean;
}) {
  const meta = SECTION_KIND_META[section.section_kind];
  const Icon = meta.icon;

  return (
    <li className="relative">
      <article className="grid grid-cols-[3rem_2.5rem_minmax(0,1fr)_auto] items-center gap-3 p-4 rounded-lg border border-border bg-white hover:shadow-sm transition-shadow">
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-lg text-slate-300 tabular-nums leading-none">
            {String(index).padStart(2, '0')}
          </span>
          <GripVertical className="h-3.5 w-3.5 text-slate-200" />
        </div>

        <span className={cn(
          'inline-flex items-center justify-center h-9 w-9 rounded-md ring-1',
          meta.style,
        )}>
          <Icon className="h-4 w-4" />
        </span>

        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={cn(
              'text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1',
              meta.style,
            )}>
              {meta.label}
            </span>
          </div>
          <div className="text-sm font-medium truncate">
            {section.title ?? <span className="italic text-muted-foreground">Untitled section</span>}
          </div>
          <div className="text-[11px] font-mono text-muted-foreground truncate mt-0.5">
            → {section.target_id.slice(0, 8)}…
          </div>
        </div>

        {canMutate && (
          <Button
            size="icon" variant="ghost"
            onClick={onDelete} disabled={removing}
            aria-label={`Remove section ${index}`}
            className="h-7 w-7"
          >
            <Trash2 className="h-3.5 w-3.5 text-slate-400" />
          </Button>
        )}
      </article>
    </li>
  );
}

function AddSectionDialog({
  courseId, customerId, nextSortOrder, open, onOpenChange,
}: {
  courseId: string;
  customerId: string;
  nextSortOrder: number;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<CourseSectionKind>('folder');
  const [targetId, setTargetId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => courses.createSection(courseId, {
      section_kind: kind,
      target_id: targetId ?? '',
      title: title.trim() || null,
      sort_order: nextSortOrder,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['course-sections', courseId] });
      qc.invalidateQueries({ queryKey: ['course', courseId] });
      setKind('folder'); setTargetId(null); setTitle(''); setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  // CourseSectionTargetPicker's `value` is the authoritative id; presence
  // is the only validity check we need (the picker only emits real rows).
  const validTarget = !!targetId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add section · {String(nextSortOrder + 1).padStart(2, '0')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as CourseSectionKind)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="folder">Folder (recordings)</SelectItem>
                <SelectItem value="conference">Conference room</SelectItem>
                <SelectItem value="quiz">Quiz</SelectItem>
                <SelectItem value="survey">Survey</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="sec-title">Title (optional)</Label>
            <Input
              id="sec-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Week 1: Introduction"
            />
          </div>
          <div className="space-y-1">
            <Label>Target</Label>
            <CourseSectionTargetPicker
              kind={kind}
              value={targetId}
              onChange={setTargetId}
              customerId={customerId}
            />
            <p className="text-[11px] text-muted-foreground">
              Pick the folder, conference room, quiz, or survey this section runs.
            </p>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            disabled={create.isPending || !validTarget}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Adding…' : 'Add section'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Enrollments ──────────────────────────────────────────────────────────────

function EnrollmentsPanel({
  course, canMutate,
}: { course: CourseRow; canMutate: boolean }) {
  const qc = useQueryClient();
  const [showCancelled, setShowCancelled] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['course-enrollments', course.id, showCancelled],
    queryFn: () => courses.listEnrollments(course.id, { active: !showCancelled, limit: 500 }),
  });
  const rows = data?.rows ?? [];

  const cancel = useMutation({
    mutationFn: (id: string) => courses.cancelEnrollment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['course-enrollments', course.id] });
      qc.invalidateQueries({ queryKey: ['course', course.id] });
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-sm">
          {showCancelled ? 'All enrollments' : 'Active enrollments'}
        </CardTitle>
        <Button
          size="sm" variant="outline"
          onClick={() => setShowCancelled((v) => !v)}
        >
          {showCancelled ? 'Hide cancelled' : 'Show cancelled'}
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center">
            <UsersIcon className="mx-auto h-6 w-6 text-slate-300 mb-2" />
            <p className="text-sm text-muted-foreground">
              No enrollments yet. Enroll callers via an IVR <code>enrollment</code> node
              or manually from a caller profile.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <EnrollmentItem
                key={r.id}
                row={r}
                canMutate={canMutate}
                onCancel={() => cancel.mutate(r.id)}
                cancelling={cancel.isPending}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function EnrollmentItem({
  row, canMutate, onCancel, cancelling,
}: {
  row: EnrollmentRow;
  canMutate: boolean;
  onCancel: () => void;
  cancelling: boolean;
}) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_10rem_10rem_auto] items-center gap-3 px-5 py-3 text-sm">
      <span className="font-mono text-xs text-muted-foreground truncate">
        {row.caller_id.slice(0, 8)}…
      </span>
      <span className="text-xs text-muted-foreground">
        enrolled {new Date(row.enrolled_at).toLocaleDateString()}
      </span>
      <span className="text-xs">
        {row.cancelled_at ? (
          <span className="text-rose-700 font-semibold uppercase tracking-wider text-[10px]">
            Cancelled
          </span>
        ) : row.completed_at ? (
          <span className="text-emerald-700 font-semibold uppercase tracking-wider text-[10px]">
            Completed
          </span>
        ) : (
          <span className="text-sky-700 font-semibold uppercase tracking-wider text-[10px]">
            Active
          </span>
        )}
      </span>
      {canMutate && !row.cancelled_at && (
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={cancelling}>
          Cancel
        </Button>
      )}
    </li>
  );
}
